import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LOGO_URL_LIGHT } from "@/data/ekipData";

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/groupe", label: t.nav.groupe, testId: "nav-groupe" },
    { to: "/ecosysteme", label: t.nav.ecosysteme, testId: "nav-ecosysteme" },
    { to: "/projets", label: t.nav.projets, testId: "nav-projets" },
    { to: "/impact", label: t.nav.impact, testId: "nav-impact" },
    { to: "/actualites", label: t.nav.actualites, testId: "nav-actualites" },
    { to: "/carrieres", label: t.nav.carrieres, testId: "nav-carrieres" },
  ];

  const solid = scrolled || !isHome;

  return (
    <header
      data-testid="ekip-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid
          ? "bg-ekip-navy/95 backdrop-blur-xl border-b border-ekip-gold/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 flex items-center justify-between h-32 md:h-40">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img
            src={LOGO_URL_LIGHT}
            alt="EKIP Holding Company"
            className="h-32 md:h-40 w-auto object-contain group-hover:opacity-90 transition-opacity"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `nav-link font-body text-[13px] tracking-wide uppercase font-medium ${
                  isActive ? "text-ekip-copper active" : "text-white/85 hover:text-ekip-copper"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <button
            onClick={toggle}
            data-testid="nav-lang-toggle"
            className="text-[12px] tracking-[0.2em] font-body font-medium text-white/70 hover:text-ekip-copper transition-colors"
            aria-label="Change language"
          >
            <span className={lang === "fr" ? "text-ekip-copper" : ""}>FR</span>
            <span className="mx-2 text-white/30">|</span>
            <span className={lang === "en" ? "text-ekip-copper" : ""}>EN</span>
          </button>
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="inline-flex items-center gap-2 bg-ekip-copper text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-ekip-copper-hover transition-colors"
          >
            <span className="w-1.5 h-1.5 bg-white" style={{ transform: "rotate(45deg)" }} />
            {t.nav.cta}
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white p-2"
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-ekip-navy border-t border-ekip-gold/20 px-6 py-8"
        >
          <nav className="flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testId}-mobile`}
                className={({ isActive }) =>
                  `font-display text-xl border-b border-ekip-gold/10 pb-3 ${
                    isActive ? "text-ekip-copper" : "text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center justify-between pt-4">
              <button
                onClick={toggle}
                data-testid="nav-lang-toggle-mobile"
                className="text-[13px] tracking-[0.2em] text-white/70"
              >
                <span className={lang === "fr" ? "text-ekip-copper" : ""}>FR</span>
                <span className="mx-2">|</span>
                <span className={lang === "en" ? "text-ekip-copper" : ""}>EN</span>
              </button>
              <Link
                to="/contact"
                data-testid="nav-cta-mobile"
                className="inline-flex items-center gap-2 bg-ekip-copper text-white px-5 py-3 text-[11px] uppercase tracking-[0.22em]"
              >
                {t.nav.cta}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
